<script lang="ts">
	import { onMount } from 'svelte';
	// import Seo from '$lib/components/common/SEO.svelte';
	import Editor from '$lib/components/editor/editor.svelte';
	import type { PostAttrs } from '$lib/posts/post.type';
	import {
		postStore,
		postOwner,
		voidCanEditPost,
		deletePost,
		addDigs,
		getDigsCount,
		setPostDigs
	} from '$lib/posts';
	import { goto } from '$app/navigation';
	import { browser } from '$app/environment';
	import { user, getAuthorUsername, getUsernameByRealname } from '$lib/user';
	import type { PageData } from './$types';
	import BlogsUnderPost from '$lib/components/blog/BlogsUnderPost.svelte';
	import Comments from '$lib/components/comments/Comments.svelte';
	import { addNotification } from '$lib/chat/chat.api';
	import DOMPurify from 'dompurify';
	import { profileStore } from '$lib/user';
	import { handleAlert } from '$lib/components/alert';
	import PostHeader from '$lib/components/editor/PostHeader.svelte';
	import PostActions from '$lib/components/editor/PostActions.svelte';
	import { communityPostsStore } from '$lib/components/blog/performantCommunityPosts';
	import PostMetadata from '$lib/components/editor/PostMetadata.svelte';

	// import AboutBlurb from '$lib/components/common/AboutBlurb.svelte';
	// ** get base64 for SEO
	// import { getPostImage } from '$lib/posts';

	// export let image_url;
	// export let alt = '...';

	// ** get base64 for SEO
	// let src;
	// let base64Image = '';

	export let data: PageData;
	let post: PostAttrs = data.post;
	let validated: boolean = data.validated;
	// export let slug;
	let replied_to_username: string;
	let post_slug = '';
	let loading = true;
	let digsLoaded = false;
	// let maxDigs = 33;
	let CLEAN_MESSAGE = 'dig';
	let created_at = new Date();
	let mentioned: [] = [];
	let channelName = '';
	let digsCount = 0;
	let id = $user?.id;
	let username = $profileStore?.username;
	let buttonText = 'Make Claim';
	let deleteConfirm = false;
	let showBountyMessage = false;
	$postStore = post;

	// Update button text based on bounty_posted status
	// $: buttonText = $postStore.bounty_posted ? 'Edit Claim' : 'Claim Bounty';

	// $: postClaimInfo = {
	// 	[post.slug]: {
	// 		id: post.claim_id,
	// 		author: post.created_by,
	// 		contributors: post.contributor_members
	// 	}
	// };

	// Function to update the post content
	async function updatePostContent() {
		$postStore = post;
		await assignAuthorUsername();
		// Check if the user can edit the post
		if ($user && browser) await voidCanEditPost($postStore);
	}

	async function handleClick() {
		// Validate user is logged in
		if (!$user?.id || !$profileStore?.id) {
			console.log('User must be logged in to dig');
			handleAlert({
				type: 'default',
				text: 'You must be logged in to dig posts'
			});
			return;
		}

		// Validate post exists
		if (!$postStore?.id) {
			console.error('Post ID is undefined');
			return;
		}

		console.log('Attempting to dig post:', {
			postId: $postStore.id,
			userId: $profileStore.id,
			username: $profileStore.username
		});

		try {
			const CLEAN_USERNAME = $profileStore.username
				? DOMPurify.sanitize($profileStore.username)
				: '';
			const replied_to_username = $postStore.author
				? await getUsernameByRealname($postStore.author)
				: '';

			const success = await addDigs($postStore.id, $profileStore.id, 1);
			console.log('Dig result:', success);

			if (success) {
				const newDigsCount = digsCount + 1;
				digsCount = newDigsCount;
				postStore.update((store) => ({ ...store, digs: newDigsCount }));
				await setPostDigs($postStore.id, newDigsCount);
				console.log('Successfully updated digs count to:', newDigsCount);
			} else {
				console.error('Failed to update digs in database');
				handleAlert({
					type: 'error',
					text: 'Failed to save dig. Please try again.'
				});
				return;
			}

			// Only send notification if we have all required data
			if (CLEAN_USERNAME && $postStore.id && $profileStore.id) {
				await addNotification(
					CLEAN_MESSAGE,
					CLEAN_USERNAME,
					mentioned,
					replied_to_username,
					false,
					$postStore.id,
					$profileStore.id,
					created_at,
					'dig',
					'post',
					$postStore.slug || '',
					channelName || ''
				);
				console.log('Notification sent for dig');
			}
		} catch (error) {
			console.error('Error in handleClick:', error);
			handleAlert({
				type: 'error',
				text: 'An error occurred while saving your dig. Please try again.'
			});
		}
	}

	async function handleClaimClick() {
		const slug = $postStore.slug;
		if (!$postStore.bounty_posted) {
			// Route to /bounty if no bounty is posted
			goto(`/bounty?report=${slug}&postId=${$postStore.id}`);
		} else {
			showBountyMessage = true;
		}
		// TODO: this needs a different approach if we're going to do this... either do a query for claimId to the contract, or pass the database information in as params
		// 	const queryParams = new URLSearchParams({
		// 		postId: $postStore.claim_id || '',
		// 		contributors: JSON.stringify($postStore.contributor_members)
		// 	});
		// 	goto(`/bounty/claim/${$postStore.claim_id}?${queryParams.toString()}`);
		// 	// }
		// }
	}

	const assignAuthorUsername = async () => {
		try {
			// Call getAuthorUsername with $postStore.created_by
			if ($postStore.created_by) {
				replied_to_username = await getAuthorUsername($postStore.created_by);
			} else {
				replied_to_username = '';
			}
			post_slug = $postStore.slug ?? '';
		} catch (error) {
			console.error('Error:', error);
			// Handle error if necessary
		}
	};

	// Reactively update the post content when the slug changes
	$: if (data.post) {
		post = data.post;
		validated = data.validated;
		updatePostContent();
	}

	onMount(async () => {
		// $commentsStore = [];
		// Initialize the post content on component mount
		// Load settings
		loading = false;

		// Load digs, share, and comments only after settings are loaded
		if (!loading) {
			digsCount = await getDigsCount($postStore.id);
			digsLoaded = true; // Mark digs as loaded
		}
		// Replace with the actual blob retrieval logic
		// const response = await fetch('image_url');
		// const blob = src;
		// console.log('blob', blob);
	});

	// afterUpdate(() => {
	// 	// Once the data has been updated, set loading to false
	// 	loading = false;
	// });

	// ** get base64 for SEO
	// function blobToBase64(blob) {
	// 	return new Promise((resolve, reject) => {
	// 		const reader = new FileReader();
	// 		reader.onerror = reject;
	// 		reader.onload = () => {
	// 			resolve(reader.result.split(',')[1]);
	// 		};
	// 		reader.readAsDataURL(blob);
	// 	});
	// }

	function handleDeleteClick() {
		if (!$user?.member) {
			handleAlert({
				type: 'default',
				text: 'You must be an active member to delete this post. Or, to request content deletion email community@bloomnetwork.earth'
			});
			goto('/auth');
			return;
		} else if ($user?.member) deleteConfirm = true;
	}

	const onDelete = async () => {
		if (!$user?.member) {
			await goto('/auth');
			return;
		}

		if ($postStore.id) {
			const success = await deletePost($postStore.id);
			if (success) {
				handleAlert({
					type: 'success',
					text: 'Post has been deleted successfully.'
				});
				communityPostsStore.reset();
				// Small delay to allow the alert to be seen before navigation
				await new Promise((resolve) => setTimeout(resolve, 1000));
				await goto('/');
			} else {
				handleAlert({
					type: 'error',
					text: 'Failed to delete post. Please try again.'
				});
				deleteConfirm = false;
			}
		} else {
			handleAlert({
				type: 'error',
				text: 'Could not delete post: Post ID not found.'
			});
			deleteConfirm = false;
		}
	};

	const onEdit = () => {
		goto(`/edit/${$postStore.slug}`);
	};

	// const buttonClass =
	// 	'border border-gradientBlueMid rounded-sm px-2 py-2 3xl:py-4 text-center w-24 xl:w-28 text-xs md:text-sm xl:text-lg tracking-wider';
</script>

<svelte:head>
	<title>{$postStore.title}</title>
	<meta name="title" content={$postStore.title} />
	<meta name="description" content={$postStore.description} />
	<meta name="keywords" content="regeneration, climate, justice, media, social, community, ReFi" />
	<meta name="author" content={$postStore.author} />
	<!-- <meta property="og:image" content={`data:image/jpeg;base64,${base64Image}`} /> -->
	<meta property="og:image" content="/img/bloomLogoExploded.png" />
	<meta property="og:description" content={$postStore.description} />
	<meta property="og:image" content="/img/bloomLogoExploded.png" />
	<meta property="og:url" content={$postStore.slug} />
	<meta property="og:type" content="article" />
	<!-- <meta property="og:article:published_time" content={$postStore.updated_at} /> -->
	<meta property="og:article:author" content={$postStore.author} />

	<meta name="twitter:card" content={$postStore.title} />
	<meta name="twitter:title" content={$postStore.title} />
	<meta name="twitter:description" content={$postStore.description} />
	<meta name="twitter:image" content="/img/bloomLogoExploded.png" />
	<meta name="twitter:creator" content={$postStore.author} />
</svelte:head>

<!-- <svelte:head>
	/ SEO Page Title /
	<Seo title={$postStore.title} description={$postStore.description} />
	/ dont use this yet / image={`data:image/jpeg;base64,${base64Image}`}
</svelte:head> -->

<!-- <meta
		property="og:image"
		content={`data:image/jpeg;base64,${base64Image}`} -->

<!-- <img class="max-h-60 rounded-2xl mx-auto object-contain" {alt} {src} /> -->
{#key $postStore.id}
	<div
		class="bg-[url('/img/modalBg3.png')] bg-opacity-75 bg-cover font-mono text-white text-left -mt-32 pt-24 pb-5 lg:pb-10
 3xl:max-w-full overflow-x-hidden rounded-t-2xl"
	>
		<!-- bg-[url('/img/modalBgLightNew.png')] dark: -->
		<div class="px-0 sm:px-4 mx-auto mt-4 xl:w-11/12 2xl:w-5/6 3xl:w-3/4 4xl:w-2/3">
			{#if ($postStore.published && ($postStore.membersonly === false || ($user?.member && $postStore.membersonly === true))) || (!$postStore.published && $postOwner)}
				<div class=" md:px-10 font-sans text-white text-left pt-5 mt-5">
					<div class="bg-cosmicPurple800 px-4 md:px-5 lg:px-7 3xl:px-16 pb-5 rounded-t-2xl pt-5">
						{#if !$postStore.published && $postOwner}
							<p class="text-cosmicPurple100 italic text-sm">
								This post is a draft. Only you can currently see this post.
							</p>
						{/if}
						{#if $postStore.type == 'action' && validated}
							{#if $postOwner}
								<p class="text-cosmicPurple100">
									✔️ This action has already been validated and editing is disabled.
								</p>
							{:else}
								<p class="text-cosmicPurple100">✔️ This action has been verified.</p>
							{/if}
						{/if}

						{#if $postStore.type === 'action' || ($postOwner && !validated)}
							<div class="flex flex-row justify-between items-center mb-2">
								{#if $postStore.type === 'action'}
									<div
										class="font-light font-sans lg:text-xl md:pt-2 text-cosmicPurple100 text-left"
									>
										REGENERATIVE ACTION
									</div>
								{/if}
								{#if $postOwner && !validated}
									<PostActions
										{deleteConfirm}
										{buttonText}
										showClaim={$postStore.type === 'action'}
										on:edit={onEdit}
										on:delete={onDelete}
										on:deleteConfirm={handleDeleteClick}
										on:claim={handleClaimClick}
									/>
								{/if}
							</div>
							{#if $postOwner && showBountyMessage && $postStore.bounty_posted}
								<p
									class="mt-4 text-sm text-cosmicPurple100 text-center max-w-[380px] border-2 border-cosmicMagenta400 rounded-lg p-2 bg-cosmicMagenta400 bg-opacity-50 mx-auto"
								>
									You have already made a bounty claim for this post. To edit it, go to your
									<a
										href="/member/{$profileStore.username}?view=claims"
										class="underline text-bloomBlueNormal text-sm"
									>
										claims
									</a>
									and edit it from there.
								</p>
							{/if}
						{/if}

						<PostHeader
							editableTitle={false}
							title={$postStore.title || ''}
							author={$postStore.author || ''}
							created_at={$postStore.created_at}
							{digsCount}
							digDisabled={false}
							shareSlug={$postStore.slug || ''}
							shareTitle={$postStore.title || ''}
							showDigs={true}
							showShare={true}
							on:dig={handleClick}
							location={$postStore.location}
						/>
						<PostMetadata post={$postStore} type={$postStore.type} view={true} />
					</div>
					<div class="w-full bg-cosmicPurplebg md:pb-10 rounded-b-2xl">
						<Editor delta={$postStore.contents ? $postStore.contents : ''} view={true} />
					</div>
				</div>
			{/if}
		</div>
	</div>
{/key}

{#if $postStore.id && digsLoaded}
	<div class="bg-cosmicPurplebg px-3 md:px-10 lg:px-5 xl:px-10 py-5 pt-5 rounded-b-2xl">
		<Comments post_id={$postStore.id} {replied_to_username} {post_slug} {id} {username} />
	</div>
{/if}

{#if $postStore.id && !loading}
	<BlogsUnderPost />
{/if}
